// The booking rate (BL-003): over a range of the company's days, by the person who took the calls,
// by reason, or by campaign. A CSR sees their own; owners and managers see everyone's, with how many
// of each person's reasons were changed in review, so a pattern stands out. By reason over a month,
// the after-hours calls a company might start a service for.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { rangeOf, rateLine, ratePct, takersNote, type RangeKey, type RateRow } from './reasons-model.ts';
import './booking-rate.css';

type By = 'csr' | 'reason' | 'campaign';
type Rate = { rows: RateRow[]; total: RateRow; missed: number; takersSince: string | null };

const RANGES: [RangeKey, string][] = [
  ['week', 'This week'],
  ['month', 'This month'],
  ['last_month', 'Last month'],
];
const BYS: [By, string][] = [
  ['csr', 'By who took them'],
  ['reason', 'By reason'],
  ['campaign', 'By campaign'],
];

/** "This week: 6 of 8 leads booked (75%)": the CSR's own line, or everyone's for a reviewer. */
export function MyRate({
  today,
  everyone,
  version,
}: {
  today: string;
  everyone: boolean;
  /** Changes when a call's reason, outcome or review may have: the line is read again (P-0052). */
  version: string;
}) {
  const [r, setR] = useState<Rate | null>(null);
  useEffect(() => {
    void client
      .call<Rate>('calls.bookingRate', rangeOf('week', today))
      .then(setR)
      .catch(() => setR(null));
  }, [today, version]);
  if (!r) return null;
  const row = everyone ? r.total : r.rows[0];
  if (!row) return null;
  return (
    <p className="my-rate" role="status">
      This week{everyone ? ', everyone' : ''}: {rateLine(row)}
    </p>
  );
}

export function BookingRate({ today, everyone }: { today: string; everyone: boolean }) {
  const [range, setRange] = useState<RangeKey>('week');
  const [by, setBy] = useState<By>('csr');
  const [r, setR] = useState<Rate | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setR(null);
    setError(null);
    void client
      .call<Rate>('calls.bookingRate', { ...rangeOf(range, today), by })
      .then(setR)
      .catch((e: unknown) =>
        setError(e instanceof RpcError ? e.message : 'Could not read the booking rate.'),
      );
  }, [range, by, today]);
  return (
    <section className="stone card booking-rate" aria-label="Booking rate">
      <div className="row between wrap">
        <h2 className="h4">Booking rate</h2>
        <div className="row wrap">
          <label className="f inline">
            <span className="flabel">When</span>
            <select className="field" value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
              {RANGES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {everyone && (
            <label className="f inline">
              <span className="flabel">Show</span>
              <select className="field" value={by} onChange={(e) => setBy(e.target.value as By)}>
                {BYS.map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      <p className="muted small">
        Booked ÷ the leads that count: a call under a minute, or with an excused reason (after hours, say),
        doesn’t count against whoever took it.
      </p>
      {error && <p className="error">{error}</p>}
      {!r && !error && <p className="muted">Reading the calls…</p>}
      {r && (
        <>
          <ul className="rate-rows">
            {(everyone ? [r.total, ...r.rows] : r.rows).map((x, i) => (
              <li
                key={`${x.key ?? 'none'}-${x.label}-${i}`}
                className={`rate-row${i === 0 && everyone ? ' total' : ''}`}
              >
                <div className="rate-head">
                  <b>{x.label}</b>
                  {by !== 'reason' && <span className="rate-pct">{ratePct(x.rate)}</span>}
                </div>
                <span>
                  {by === 'reason' ? `${x.taken} ${x.taken === 1 ? 'call' : 'calls'}` : rateLine(x)}
                </span>
                <span className="muted small">
                  {[
                    `${x.taken} taken`,
                    x.excused ? `${x.excused} excused` : null,
                    x.underAMinute ? `${x.underAMinute} under a minute` : null,
                    x.notSet ? `${x.notSet} with no reason yet` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {x.changedInReview > 0 && (
                  <span className="chip say warn">
                    {x.changedInReview} {x.changedInReview === 1 ? 'reason' : 'reasons'} changed in review
                  </span>
                )}
              </li>
            ))}
          </ul>
          {everyone && r.missed > 0 && (
            <p className="muted small">
              {r.missed} missed {r.missed === 1 ? 'call' : 'calls'}: nobody took{' '}
              {r.missed === 1 ? 'it' : 'them'}.
            </p>
          )}
          {by === 'csr' && takersNote(rangeOf(range, today).from, r.takersSince, r.rows) && (
            <p className="muted small">{takersNote(rangeOf(range, today).from, r.takersSince, r.rows)}</p>
          )}
          {!r.rows.length && <p className="muted">No calls taken in this range.</p>}
        </>
      )}
    </section>
  );
}
